import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const STEPS = [
  { id: "BUSINESS_INFO", name: "Business Info", path: "/dashboard/onboarding" },
  { id: "AGREEMENT", name: "Agreement", path: "/dashboard/onboarding/agreement" },
  { id: "PROFILE_CONTENT", name: "Profile", path: "/dashboard/onboarding/profile" },
  { id: "PORTFOLIO", name: "Portfolio", path: "/dashboard/onboarding/portfolio" },
  { id: "PAYMENTS", name: "Payments", path: "/dashboard/onboarding/payments" },
  { id: "READY_TO_PUBLISH", name: "Publish", path: "/dashboard/onboarding/publish" }
];

const OnboardingLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOnboardingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOnboardingData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/onboarding/data`, { headers });
      setOnboardingData(response.data);
      
      // Redirect to current step if not already there
      const currentStep = response.data.photographer.onboarding_step || "BUSINESS_INFO";
      const stepConfig = STEPS.find(s => s.id === currentStep);
      
      // Only redirect if we're on the base onboarding path
      if (location.pathname === "/dashboard/onboarding" && currentStep !== "BUSINESS_INFO") {
        navigate(stepConfig.path, { replace: true });
      }
    } catch (error) {
      console.error("Failed to fetch onboarding data:", error);
      toast.error("Failed to load onboarding data");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStepIndex = () => {
    const currentStep = onboardingData?.photographer?.onboarding_step || "BUSINESS_INFO";
    return STEPS.findIndex(s => s.id === currentStep);
  };

  const getStepStatus = (stepIndex) => {
    const currentIndex = getCurrentStepIndex();
    if (stepIndex < currentIndex) return "complete";
    if (stepIndex === currentIndex) return "current";
    return "upcoming";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-layout">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Camera className="w-8 h-8 text-primary" />
            <span className="text-xl font-serif">360Lumina</span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">Setup Your Profile</span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="border-b border-border bg-card/30">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const status = getStepStatus(index);
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                        transition-all duration-300
                        ${status === "complete" 
                          ? "bg-green-500 text-white" 
                          : status === "current"
                            ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                            : "bg-muted text-muted-foreground"
                        }
                      `}
                    >
                      {status === "complete" ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span
                      className={`
                        mt-2 text-xs font-medium hidden sm:block
                        ${status === "current" ? "text-primary" : "text-muted-foreground"}
                      `}
                    >
                      {step.name}
                    </span>
                  </div>
                  
                  {/* Connector Line */}
                  {index < STEPS.length - 1 && (
                    <div className="flex-1 mx-2">
                      <div
                        className={`
                          h-1 rounded-full transition-colors duration-300
                          ${status === "complete" ? "bg-green-500" : "bg-muted"}
                        `}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <Outlet context={{ onboardingData, refreshData: fetchOnboardingData }} />
        </motion.div>
      </main>
    </div>
  );
};

export default OnboardingLayout;
